<template>
  <component
    :is="componentType"
    class="flex h-full w-full"
    :rel="link?.rel"
    :target="link?.target"
    :to="link?.href"
    @click="onGlobalLinkClick"
  >
    <Background
      :background
      class="w-full"
      :class="{
        'w-256 md:w-[352px]': fixedWidth && size === 'medium',
        'w-256': fixedWidth && size === 'small',
        'p-16': withPadding,
        'shadow-short': !!background && link?.href,
      }"
      container-class="flex flex-col gap-24 overflow-hidden"
      data-test="modular-card-container"
      rounded="always"
    >
      <ModularCardSection
        v-for="(section, sectionIndex) in parsedSections"
        :key="sectionIndex"
        v-bind="section"
        :blockPosition
        :blockTitle
        :cardPosition
        class="w-full"
        :data-test="`section-container-${sectionIndex}`"
      />
    </Background>
  </component>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import type { _Block } from '@backmarket/http-api/src/api-specs-content/models/block'
import type {
  ModularCard,
  ModularCardSection as ModularCardSectionType,
} from '@backmarket/http-api/src/api-specs-content/models/modular-card'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButtonBase } from '@ds/components/ButtonBase'

import type { ContentBlockProps } from '../../models/content-block'
import Background from '../../shared-components/Background/Background.vue'

import ModularCardSection from './ModularCardSection.vue'

export type ModularCardProps = Omit<ModularCard, 'id'> & {
  cardPosition: number
  blockTitle?: string
  fixedWidth?: boolean
  withPadding?: boolean
}

const props = withDefaults(
  defineProps<ModularCardProps & ContentBlockProps>(),
  {
    blockTitle: undefined,
    fixedWidth: true,
    withPadding: true,
    size: 'medium',
  },
)

const { trackClick } = useTracking()

const parsedSections = computed<ModularCardSectionType[]>(() => {
  if (!props.link) return props.sections

  return props.sections.map((section) => {
    const parsedSlots = section.slots.map((slot) => {
      if (slot.type === 'Button') {
        const slotProps = slot.props

        // Note: make sure to destructure slot.props to avoid mutating the props

        return {
          ...slot,
          props: {
            ...slotProps,
            ctaLink: {
              ...slotProps.ctaLink,
              link: {
                ...slotProps.ctaLink.link,
                href: '',
              },
            },
          },
        }
      }

      const blockProps = { id: slot.id, hiddenDevices: slot.hiddenDevices }

      if (slot.type === 'RichText') {
        const richTextProps = slot.props

        return {
          ...blockProps,
          type: slot.type,
          props: { ...richTextProps, ignoreLinks: true },
        } satisfies _Block<'RichText'>
      }

      if (slot.type === 'Media') {
        const mediaProps = slot.props

        return {
          ...blockProps,
          type: slot.type,
          props: { ...mediaProps, link: undefined, ignoreLinks: true },
        } satisfies _Block<'Media'>
      }

      if (slot.type === 'ModularCardImage') {
        const modularCardImage = slot.props

        // Note: make sure to destructure slot.props to avoid mutating the props
        return {
          ...slot,
          props: { ...modularCardImage, link: undefined },
        } satisfies _Block<'ModularCardImage'>
      }

      return slot
    })

    return { ...section, slots: parsedSlots }
  })
})

const componentType = computed(() => (props.link ? RevButtonBase : 'div'))

function onGlobalLinkClick() {
  if (componentType.value === 'div') return

  trackClick({
    position: props.cardPosition,
    zone: props.tracking?.zone || '',
    value: 'ModularCard',
    blockPosition: props.blockPosition || 1,
    technicalName: props.technicalName || '',
    ...(props.blockTitle && { blockTitle: props.blockTitle }),
  })
}

// This event is common to every CMS block in order to hide the block containers
// on demand
defineEmits(['error'])
</script>
